// TODO: Some argument types are ": <primitive type> | undefined". It's not necessarily
// good to allow undefined for all those methods. It was just easier (the undefined
// part comes from the API / ApiClient.ts)

import { defineStore } from 'pinia';
import { toRaw } from 'vue';
import { isUnchanged } from '@/store/cache-primitives';
import { useProducts } from './products';
import { useUser } from './user';
import { retryOnNumberLockTimeout } from '@lib/api-error';
import {
  Sorting,
  type SetColorArgs,
  type AddArticleArgs,
  type ArticleCertificationDto,
  type ArticleComplianceDto,
  type ArticleDto,
  type ArticleSeasonTimelineDto,
  type ColorDto,
  type Gender,
  type CustomFieldIdWithValueDto,
  type UpdateSkusBulkArgs,
  type WeightDto,
  type WeightPatchDto,
  type UpdateSkuSeasonPricesArgs,
  type UpdateDropsArgsItem,
  type MassUpdateImagesImage,
  type UpdateImagesImage,
  type UpdateSupplierArgs,
} from '@/ApiClient';
import { useDrops } from '@/store/drops';
import { useArticleCards } from '@/store/article-cards';
import { useArticleTable } from '@/store/article-table';
import { useActiveSeason } from '@/store/active-season';
import { signalrListen } from '@lib/signalr';
import { CACHE_TTL, onTabResume } from '@/store/cache-ttl';
import { runWithBomDataLossConfirm } from '@lib/bom-data-loss';
import { partialArticleFromCard, partialArticleFromTableRow } from '@lib/partial-article';
import { existingColorArgs } from '@lib/color-picker-draft';

type ProductArticles = { [productId: number]: ArticleDto[] };

let overviewCachedAt = 0;

function isOverviewExpired() {
  return overviewCachedAt > 0 && Date.now() - overviewCachedAt > CACHE_TTL;
}

// Note: no tab-resume clearing here. isOverviewExpired() is wall-clock based, so after a
// long absence cacheOverviewArticles() starts from a clean slate on its next write — while
// anything already rendered keeps showing the cached data instead of flashing skeletons.

// Per-product in-flight dedup so concurrent ensure calls and background revalidations
// share a single request.
const pendingLoads = new Map<number, Promise<ArticleDto[]>>();

type ArticlesSnapshot = {
  productArticles?: ProductArticles;
  overviewArticles?: Record<number, ArticleDto>;
};

// In-flight hover prefetches, keyed by article id. Outside the store state on
// purpose: these are transient request handles, not data worth reacting to.
const pendingArticleFetches = new Map<number, Promise<unknown>>();

export const useArticles = defineStore('articles', {
  state: () => ({
    productArticles: {} as ProductArticles,
    overviewArticles: {} as Record<number, ArticleDto>,
    // Slim articles projected from a list row so a details view can paint before its own
    // fetch lands (see lib/partial-article.ts). Deliberately kept apart from the two caches
    // above: they hold complete DTOs that other code is entitled to trust, and only those
    // two are persisted to IndexedDB.
    partialArticles: {} as Record<number, ArticleDto>,
  }),
  getters: {
    // Snapshot persistence contract — see store/warm-boot.ts
    snapshotDto: () => 'ArticleDto' as const,

    getById: (state) => {
      return (articleId: number): ArticleDto | undefined => {
        const overview = state.overviewArticles[articleId];
        if (overview) return overview;

        for (const productId in state.productArticles) {
          const article = state.productArticles[productId].find((a) => a.id === articleId);
          if (article) {
            return article;
          }
        }

        return state.partialArticles[articleId];
      };
    },

    /**
     * Whether `getById` would return a complete article rather than a seeded row.
     * Anything reading SKUs, seasonalities or orders must check this first — a partial
     * carries those as empty arrays.
     */
    hasFullArticle: (state) => {
      return (articleId: number): boolean => {
        if (state.overviewArticles[articleId]) return true;

        for (const productId in state.productArticles) {
          if (state.productArticles[productId].some((a) => a.id === articleId)) return true;
        }

        return false;
      };
    },
    getOverviewArticle: (state) => {
      return (articleId: number): ArticleDto | undefined => {
        return state.overviewArticles[articleId];
      };
    },
  },

  actions: {
    // Snapshot persistence contract — see store/warm-boot.ts. Both records hold
    // ArticleDto, so one snapshot covers them under a single schema hash.
    snapshotData(): ArticlesSnapshot {
      // toRaw: IndexedDB's structured clone can't handle Vue's reactive proxies
      return { productArticles: toRaw(this.productArticles), overviewArticles: toRaw(this.overviewArticles) };
    },

    seedSnapshot(data: unknown) {
      if (Object.keys(this.productArticles).length > 0 || Object.keys(this.overviewArticles).length > 0) return;
      const snapshot = data as ArticlesSnapshot;
      this.productArticles = snapshot.productArticles ?? {};
      this.overviewArticles = snapshot.overviewArticles ?? {};
    },

    cacheOverviewArticles(articles: ArticleDto[]) {
      if (isOverviewExpired()) this.overviewArticles = {};
      for (const article of articles) {
        this.overviewArticles[article.id] = article;
      }
      overviewCachedAt = Date.now();
    },

    updateProductArticle(article: ArticleDto) {
      const cardCache = useArticleCards();
      if (cardCache.articles[article.id]) {
        cardCache.update(cardCache.deriveFromArticleDto(article));
      }

      if (this.overviewArticles[article.id]) {
        this.overviewArticles[article.id] = article;
      }

      if (!this.productArticles?.[article.product.id]) return;
      this.productArticles[article.product.id] = this.productArticles[article.product.id].map((a) =>
        a.id === article.id ? { ...article } : a
      );
    },

    /**
     * One season's timeline plan, as a gate write left it. Folded into the article the same way
     * every other write is, so the tab, the cards and the board all move together — without it a
     * tick shows only where it was made and every other surface keeps yesterday's plan until the
     * article is reloaded.
     */
    patchSeasonTimeline(articleId: number, seasonId: number, timeline: ArticleSeasonTimelineDto) {
      // Resolved the way every other reader resolves an article, which is what makes this work from
      // the products list: an article opened from there is in overviewArticles and in no product's
      // list, so looking only there found nothing and folded the write into nowhere.
      const article = this.getById(articleId);
      if (!article) return;

      this.updateProductArticle({
        ...article,
        seasonalities: article.seasonalities.map((s) => (s.id === seasonId ? { ...s, timeline } : s)),
      });
    },

    async addArticleToProduct(
      productId: number,
      { color, seasonId }: { color?: ColorDto | null; seasonId?: number | null } = {}
    ) {
      const data: AddArticleArgs = { productId };
      if (color) {
        data.color = existingColorArgs(color);
      }
      if (seasonId) {
        data.seasonId = seasonId;
      }
      const article = await retryOnNumberLockTimeout(() => this.$api.article_AddArticle(useUser().brandId, data));
      this.productArticles[productId].push(article);

      return article;
    },

    async getArticle(articleId: number, productId?: number) {
      const article = await this.$api.article_GetArticle(useUser().brandId, articleId);

      // Cache the article so getById can find it, even when no productId was
      // passed (e.g. when the flyout is opened directly from a URL).
      const pid = productId ?? article.product.id;
      this.productArticles[pid] = this.productArticles[pid] ?? [];
      const index = this.productArticles[pid].findIndex((a) => a.id === article.id);
      if (index === -1) {
        this.productArticles[pid].push(article);
      } else if (!isUnchanged(this.productArticles[pid][index], article)) {
        // Revalidation: take the fresh article, but keep the existing object when the
        // response is identical so watchers on it don't refire on every open.
        this.productArticles[pid][index] = article;
      }

      delete this.partialArticles[article.id];

      // Hand back the stored instance, which on an unchanged revalidation is the object
      // callers already hold rather than the fresh one.
      return index === -1 ? article : this.productArticles[pid][index];
    },

    /**
     * Project the article out of whichever list the user is looking at (table rows or
     * board cards) so a details view can render immediately instead of waiting behind a
     * skeleton. Returns whether there is now anything to render; the caller still has to
     * fetch the real article behind it.
     */
    seedPartialArticle(articleId: number): boolean {
      if (this.hasFullArticle(articleId)) return true;
      if (this.partialArticles[articleId]) return true;

      const partial =
        partialArticleFromTableRow(useArticleTable().articles[articleId]) ??
        partialArticleFromCard(useArticleCards().articles[articleId]);
      if (!partial) return false;

      this.partialArticles[articleId] = partial;
      return true;
    },

    dropPartialArticle(articleId: number) {
      delete this.partialArticles[articleId];
    },

    /**
     * Fire-and-forget warm-up for hover prefetch, mirroring
     * `useProducts().prefetchProduct`. The details flyout awaits `getArticle`
     * behind a skeleton, so without this the panel slides in showing the
     * skeleton and swaps to content mid-animation — which is why the first open
     * of any article looks like a jump and every later one is smooth.
     *
     * Cache-checked and request-deduped, as `v-prefetch` requires: sweeping a
     * list fires this per row, and it may fire repeatedly for the same row.
     */
    prefetchArticle(articleId: number, productId?: number) {
      if (this.hasFullArticle(articleId) || pendingArticleFetches.has(articleId)) return;

      const request = this.getArticle(articleId, productId)
        .catch(() => {})
        .finally(() => pendingArticleFetches.delete(articleId));

      pendingArticleFetches.set(articleId, request);
    },

    async deleteArticle(id: number) {
      return await this.$api.article_DeleteArticle(useUser().brandId, id);
    },

    /** One request for the lot: the overview's mass delete removes them all and saves once. */
    async deleteArticles(ids: number[]) {
      if (!ids.length) return;

      await this.$api.overview_MassDeleteArticles(useUser().brandId, {
        filters: {},
        sortingOptions: { sorting: Sorting.ArticleNumber, descending: false },
        selection: { isAllSelected: false, selectedIds: ids, excludedIds: [] },
        selectedCount: ids.length,
      });
    },

    async cloneArticles(ids: number[]) {
      const articles = await this.$api.article_CloneArticles(useUser().brandId, ids);
      return articles;
    },

    setArticlesForProduct(productId: number, articles: ArticleDto[]) {
      this.productArticles[productId] = articles;
    },

    addArticleToStore(productId: number, article: ArticleDto) {
      if (!this.productArticles[productId]) {
        this.productArticles[productId] = [];
      }

      this.productArticles[productId].push(article);
    },

    async loadArticlesForProduct(productId: number) {
      const pending = pendingLoads.get(productId);
      if (pending) return pending;

      const load = (async () => {
        try {
          const articles = await this.$api.product_GetArticlesForProduct(useUser().brandId, productId);
          // Keep the existing array when revalidation returns identical data, so watchers
          // on the article list don't refire on every visit.
          const existing = this.productArticles[productId];
          if (existing && isUnchanged(existing, articles)) {
            return existing;
          }
          this.productArticles[productId] = articles;
          return articles;
        } finally {
          pendingLoads.delete(productId);
        }
      })();
      pendingLoads.set(productId, load);
      return load;
    },

    async ensureArticlesForProduct(productId: number) {
      // Articles already loaded for this product, or a complete set from the overview cache
      let cached = this.productArticles[productId];
      if (!cached || cached.length === 0) {
        const fromOverview = Object.values(this.overviewArticles).filter((a) => a.product.id === productId);
        if (fromOverview.length > 0 && fromOverview[0].product.numberOfArticles === fromOverview.length) {
          this.productArticles[productId] = fromOverview;
          cached = fromOverview;
        }
      }

      if (cached && cached.length > 0) {
        // Stale-while-revalidate: hand out the cached articles immediately so the page
        // renders without waiting, and refresh them in the background.
        this.loadArticlesForProduct(productId).catch(() => {});
        return cached;
      }

      return this.loadArticlesForProduct(productId);
    },

    async updateGenders(articleId: number, genders: Gender[]) {
      const { article, product } = await this.$api.article_UpdateGenders(useUser().brandId, articleId, { genders });
      useProducts().setProduct(product!);
      this.updateProductArticle(article!);
    },

    async updateGenderNSize(articleId: number, genderAndSizes: any) {
      // genderAndSizes = {
      //   genders: [1,2],
      //   sizeStore: ... // Optional - if here then use this one instead of sizes
      //   sizeValues: ['34', '35', '36', '37']
      // };

      let article, product, updateError, articles;

      const productId = genderAndSizes.productId;

      if (genderAndSizes.sizeStore) {
        // Gender
        ({ article } = await this.$api.article_UpdateGenders(useUser().brandId, articleId, {
          genders: genderAndSizes.genders,
        }));

        // Sizes
        const sizeData = {
          sizeSystemVersionId: genderAndSizes.sizeStore.sizeSystemVersionId,
          sizes: genderAndSizes.sizeStore.sizes,
          articleSkus: genderAndSizes.sizeStore.articleSkus,
        };

        ({ product, articles, updateError } = await this.$api.product_UpdateSizes(
          useUser().brandId,
          productId,
          sizeData
        ));

        // Use the returned articles directly
        article = articles.find((a) => a.id === articleId);
        this.setArticlesForProduct(productId, articles);
        useProducts().setProduct(product);

        this.updateProductArticle(article!);
      } else {
        ({ article, product } = await this.$api.article_UpdateGenderAndSizes(
          useUser().brandId,
          articleId,
          genderAndSizes
        ));

        useProducts().setProduct(product);
        this.updateProductArticle(article!);
      }

      return { article, product, articles, updateError };
    },

    async updateDescription(id: number, description: string) {
      const article = await this.$api.article_UpdateDescription(useUser().brandId, id, { description });

      this.updateProductArticle(article!);

      return { article };
    },

    async updateMChart(articleId: number, mChartId: number | undefined) {
      const article = await this.$api.article_UpdateMChart(useUser().brandId, articleId, { mChartId });
      this.updateProductArticle(article);
      return article;
    },

    async bulkUpdateMChart(items: { articleId: number; mChartId: number | undefined }[]) {
      await this.$api.article_BulkUpdateMChart(
        useUser().brandId,
        items.map((i) => ({ articleId: i.articleId, mChartId: i.mChartId }))
      );
    },

    async updateHierarchyAndProductCategory(
      {
        articleId,
        hierarchyNodeId,
        productCategoryId,
      }: { articleId: number; hierarchyNodeId: number; productCategoryId: number | undefined },
      { reloadProduct }: { reloadProduct: boolean } = { reloadProduct: false }
    ) {
      const { article, productUpdated } = await this.$api.article_UpdateHierarchyAndProductCategory(
        useUser().brandId,
        articleId,
        {
          hierarchyNodeId,
          productCategoryId,
        }
      );

      if (productUpdated && reloadProduct) {
        await useProducts().loadProduct(article!.product.id);
      }

      this.updateProductArticle(article!);

      return { article };
    },

    async updateArticleNumber(articleId: number, articleNumber: string) {
      const article = await this.$api.article_UpdateArticleNumber(useUser().brandId, articleId, { articleNumber });

      this.updateProductArticle(article);
    },

    async updateTags(
      articleId: number,
      tagIds: number[],
      { reloadProduct, seasonId }: { reloadProduct: boolean; seasonId?: number } = { reloadProduct: false }
    ) {
      const article = await this.$api.article_UpdateTags(useUser().brandId, articleId, { tagIds, seasonId });

      if (reloadProduct) {
        await useProducts().loadProduct(article.product.id);
      }

      this.updateProductArticle(article);

      return article;
    },

    async massAddRemoveTags(
      articleIds: number[],
      seasonId: number | null,
      tagIdToAddList: number[],
      tagIdToRemoveList: number[]
    ) {
      const articles = await this.$api.article_MassAddRemoveTags(useUser().brandId, articleIds, {
        seasonId: seasonId ?? undefined,
        tagIdToAddList,
        tagIdToRemoveList,
      });

      for (const article of articles) {
        this.updateProductArticle(article);
      }

      return articles;
    },

    async updateSeasons(
      articleId: number,
      seasonIds: number[],
      { reloadProduct }: { reloadProduct: boolean } = { reloadProduct: false }
    ) {
      const result = await runWithBomDataLossConfirm((suppressBomDropWarning) =>
        this.$api.article_UpdateSeasons(useUser().brandId, articleId, { seasonIDs: seasonIds, suppressBomDropWarning })
      );
      if (result === null) return null;

      const { article, productUpdated } = result;

      if (reloadProduct && productUpdated) {
        await useProducts().loadProduct(article.product.id);
      }

      this.updateProductArticle(article);

      return { article, productUpdated };
    },

    async updateSuppliers(
      articleId: number,
      supplierUpdates: UpdateSupplierArgs[],
      seasonId?: number,
      { reloadProduct }: { reloadProduct: boolean } = { reloadProduct: false }
    ) {
      const article = await this.$api.article_UpdateSuppliers(useUser().brandId, articleId, seasonId, supplierUpdates);

      if (reloadProduct) {
        await useProducts().loadProduct(article.product.id);
      }

      this.updateProductArticle(article);

      return { article };
    },

    async updateOrder(articleId: number, deliveryId: number, quantity: number) {
      await this.$api.article_UpdateDelivery(useUser().brandId, articleId, deliveryId, { quantity });
    },

    async updateOrders(
      articleId: number,
      deliveries: any,
      { reloadProduct }: { reloadProduct: boolean } = { reloadProduct: false }
    ) {
      const simplifiedOrders = deliveries.map((order: any) => {
        const simplifiedOrder = {
          id: order.id,
          quantity: order.quantity,
        };

        // New order
        if (!order.id) {
          Object.assign(simplifiedOrder, {
            inboundDate: order.inboundDate,
            typeId: order.type?.id,
          });
        }

        return simplifiedOrder;
      });

      const { article, ordersUpdated } = await this.$api.article_UpdateDeliveries(
        useUser().brandId,
        articleId,
        simplifiedOrders
      );

      if (reloadProduct && ordersUpdated) {
        await useProducts().loadProduct(article.product.id);
      }

      this.updateProductArticle(article);

      return { article, ordersUpdated };
    },

    async updateDrops(
      articleId: number,
      dropData: UpdateDropsArgsItem[] = [],
      { reloadProduct }: { reloadProduct: boolean } = { reloadProduct: false }
    ) {
      const { article, productUpdated } = await this.$api.article_UpdateDrops(useUser().brandId, articleId, dropData);

      if (reloadProduct && productUpdated) {
        await useProducts().loadProduct(article!.product.id);
      }
      await useDrops().loadAll();
      this.updateProductArticle(article!);

      return { article, productUpdated };
    },

    async updateSeasonPricesMultipleArticles(
      args: UpdateSkuSeasonPricesArgs,
      { reloadProduct }: { reloadProduct: boolean } = { reloadProduct: false }
    ) {
      const article = await this.$api.sku_UpdateSkuSeasonPrices(useUser().brandId, args);

      if (reloadProduct) {
        await useProducts().loadProduct(article.product.id);
      }

      this.updateProductArticle(article);

      return { article };
    },

    async updateComment(articleId: number, comment: string) {
      const article = await this.$api.article_UpdateComment(useUser().brandId, articleId, { comment });

      this.updateProductArticle(article);

      return { article };
    },

    async updateCertifications(articleId: number, certifications: ArticleCertificationDto[]) {
      const article = await this.$api.article_UpdateArticleCertifications(useUser().brandId, articleId, certifications);

      this.updateProductArticle(article);

      return { article };
    },

    async updateCompliances(articleId: number, compliances: ArticleComplianceDto[]) {
      const article = await this.$api.article_UpdateArticleCompliances(useUser().brandId, articleId, compliances);

      this.updateProductArticle(article);

      return { article };
    },

    async updateStatus(
      articleId: number,
      statusId: number,
      { reloadProduct }: { reloadProduct: boolean } = { reloadProduct: false }
    ) {
      const { article, productUpdated, missingProperties } = await this.$api.article_UpdateStatus(
        useUser().brandId,
        articleId,
        {
          statusId,
        }
      );

      // Missing required properties
      if (missingProperties!.required!.length) {
        return { missingProperties };
      }

      if (reloadProduct && productUpdated) {
        await useProducts().loadProduct(article!.product.id);
      }

      this.updateProductArticle(article!);

      return { article, productUpdated, missingProperties };
    },

    async updateColor(
      articleId: number,
      args: SetColorArgs | null,
      { reloadProduct }: { reloadProduct: boolean } = { reloadProduct: false }
    ) {
      const article = await this.$api.article_UpdateColor(useUser().brandId, articleId, args ?? undefined);

      if (reloadProduct) {
        await useProducts().loadProduct(article.product.id);
      }

      this.updateProductArticle(article);

      return { article };
    },
    // The authoritative per-SKU write. Returns the whole article so the article-level summary
    // recomputes from the same response.
    async updateSkuWeight(brandId: number, skuId: number, weight: WeightPatchDto) {
      const article = await this.$api.sku_SetWeight(brandId, skuId, weight);

      this.updateProductArticle(article);

      return { article };
    },
    async updateWeight(articleId: number, weight: WeightPatchDto) {
      const article = await this.$api.article_UpdateWeight(useUser().brandId, articleId, weight);

      this.updateProductArticle(article);

      return { article };
    },
    async updateTariffCodes(articleId: number, tariffCodeIds: number[]) {
      const article = await this.$api.article_UpdateTariffCodes(useUser().brandId, articleId, tariffCodeIds);

      this.updateProductArticle(article);

      return { article };
    },
    async updateImages(
      articleId: number,
      images: UpdateImagesImage[],
      { reloadArticles }: { reloadArticles: boolean } = { reloadArticles: false }
    ) {
      const { article, product } = await this.$api.article_UpdateImages(useUser().brandId, articleId, images);

      useProducts().setProduct(product);

      if (reloadArticles) {
        await this.loadArticlesForProduct(product.id);
      } else {
        if (this.productArticles[product.id].length) {
          this.updateProductArticle(article!);
        }
      }

      return article;
    },
    async massUpdateImages(articleIds: number[], images: UpdateImagesImage[]) {
      // Tags are not part of this endpoint — it sets which images the articles
      // have and their order. Dropped here rather than sent and ignored.
      const withoutTags: MassUpdateImagesImage[] = images.map(({ id, azureBlobFileId }) => ({
        id,
        azureBlobFileId,
      }));
      const { articles, product } = await this.$api.article_MassUpdateImages(useUser().brandId, {
        articleIds,
        images: withoutTags,
      });

      await useProducts().setProduct(product);

      const updatedProductArticles = this.productArticles[product.id].map(
        (article) => articles.find((updatedArticle) => updatedArticle.id === article.id) || article
      );

      this.productArticles[product.id] = updatedProductArticles;

      return articles;
    },
    async updatePrincipalType(
      articleId: number,
      subTypeId: number | undefined,
      slotId: number | undefined,
      seasonId?: number
    ) {
      const article = await this.$api.article_UpdatePrincipalMaterial(useUser().brandId, articleId, {
        subTypeId: subTypeId,
        slotId,
        seasonId,
      });

      this.updateProductArticle(article);

      return { article };
    },

    async generateSelectedSkuCodes(articleId: number, targets: { skuCodeId: number; skuIds: number[] }[]) {
      try {
        for (const { skuCodeId, skuIds } of targets) {
          await this.$api.skuCode_GenerateSkuCodeValuesForSkus(useUser().brandId, skuCodeId, { skuIds, force: false });
        }
      } finally {
        // Earlier code types may have succeeded even if a later range is exhausted.
        const article = await this.$api.article_GetArticle(useUser().brandId, articleId);
        this.updateProductArticle(article);
      }
    },

    async generateSkuCodes(brandId: number, articleId: number, skuCodeIds: number[]) {
      const article = await this.$api.article_GenerateSkuCodeValuesForSkus(brandId, articleId, {
        skuCodeIds: skuCodeIds,
      });

      this.updateProductArticle(article);

      return { article };
    },
    async setSkuCodeValue(brandId: number, skuId: number, skuCodeId: number, value: string | null | undefined) {
      value = value || undefined;

      const article = await this.$api.sku_SetSkuCodeValue(brandId, skuId, skuCodeId, { value });

      this.updateProductArticle(article);

      return { article };
    },

    /**
     * The bulk cell write behind the SKU table's paste, undo and multi-cell edit. One request for
     * the whole block, and the article comes back so the cells hold their new values as soon as it
     * settles rather than reverting until SignalR lands.
     */
    async updateSkusBulk(brandId: number, args: UpdateSkusBulkArgs) {
      const article = await this.$api.sku_UpdateSkusBulk(brandId, args);

      this.updateProductArticle(article);

      return { article };
    },

    async saveCustomFieldsSku(skuId: number, seasonId: number | null, values: CustomFieldIdWithValueDto[]) {
      const article = await this.$api.sku_UpdateCustomFieldValues(useUser().brandId, skuId, seasonId, values);

      this.updateProductArticle(article);

      return article;
    },

    async updateCustomFields(articleId: number, seasonId: number | null, values: CustomFieldIdWithValueDto[]) {
      const article = await this.$api.article_UpdateCustomFieldValues(useUser().brandId, articleId, seasonId, values);

      this.updateProductArticle(article);
      return article;
    },
    async updateCustomFieldsSku(skuId: number, seasonId: number | null, values: CustomFieldIdWithValueDto[]) {
      const brandId = useUser().brandId;

      // Don't need to update product, because product doesn't care about SKU-level custom fields atm.
      return await this.$api.sku_UpdateCustomFieldValues(brandId, skuId, seasonId, values);
    },
    listen() {
      signalrListen(
        'ArticlesUpdated',
        async (articleIds: number[]) => {
          const cardCache = useArticleCards();
          const tableCache = useArticleTable();
          const productsStore = useProducts();

          // Editing a grouped BOM can fan a single ArticlesUpdated event out to 100+ ids.
          // Collect the ones we actually have cached, then refresh them with one bulk request
          // per cache instead of a sequential per-article round-trip (which flooded the network
          // and made the page laggy while the requests drained one by one).
          const articleIdsToFetch: number[] = [];
          const tableIdsToFetch: number[] = [];

          for (const articleId of articleIds) {
            if (this.hasFullArticle(articleId) || cardCache.articles[articleId]) articleIdsToFetch.push(articleId);
            if (tableCache.articles[articleId]) tableIdsToFetch.push(articleId);
          }

          if (articleIdsToFetch.length === 0 && tableIdsToFetch.length === 0) return;

          const brandId = useUser().brandId;
          const activeSeasonId = useActiveSeason().activeSeasonId ?? undefined;

          // Deleted articles are simply absent from the bulk responses, so no per-id error handling needed.
          const [articles, tableArticles] = await Promise.all([
            articleIdsToFetch.length
              ? this.$api.article_GetArticles(useUser().brandId, articleIdsToFetch)
              : Promise.resolve([]),
            tableIdsToFetch.length
              ? this.$api.overview_GetTableArticles(brandId, activeSeasonId, tableIdsToFetch)
              : Promise.resolve([]),
          ]);

          const reloadedProductIds = new Set<number>();

          for (const article of articles) {
            this.updateProductArticle(article);

            // Reload the product if it's cached (e.g. product page open in another tab)
            const productId = article.product.id;

            if (productsStore.loaded[productId] && !reloadedProductIds.has(productId)) {
              reloadedProductIds.add(productId);
              productsStore.loadProduct(productId);
            }
          }

          for (const tableArticle of tableArticles) {
            tableCache.update(tableArticle);
          }
        },
        { listenForever: true }
      );

      signalrListen(
        'ArticlesDeleted',
        (articleIds: number[]) => {
          const cardCache = useArticleCards();
          const tableCache = useArticleTable();

          for (const articleId of articleIds) {
            cardCache.remove(articleId);
            tableCache.remove(articleId);
            delete this.overviewArticles[articleId];
            delete this.partialArticles[articleId];

            for (const productId in this.productArticles) {
              const index = this.productArticles[productId].findIndex((a) => a.id === articleId);
              if (index !== -1) {
                this.productArticles[productId].splice(index, 1);
                break;
              }
            }
          }
        },
        { listenForever: true }
      );
    },
  },
});
